import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  CheckCircleOutline,
  Close,
  CloudUpload,
  DeleteOutline,
  PlayCircleOutline,
  Sync,
  Videocam
} from '@mui/icons-material';
import StyledPlayDrawer from 'common/theme/play/StyledPlayDrawer';
import PlayRecoveryStepEditor from 'components/play/admin/PlayRecoveryStepEditor';
import VideoRecorderModal from 'components/play/admin/VideoRecorderModal';
import VideoPreviewModal from 'components/play/admin/VideoPreviewModal';
import VideoTrimField from 'components/play/admin/VideoTrimField';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import { MAX_VIDEO_MB, MAX_VIDEO_SECONDS, validateVideoFile } from 'components/play/admin/mediaLimits';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import {
  ADMIN_PLAY_RECOVERY_PRACTICES,
  PLAY_RECOVERY_PRACTICES,
  SAVE_PLAY_RECOVERY_PRACTICE
} from 'api/graphql/playRecovery';
import { ADMIN_PLAY_MUSIC_ALBUMS, ADMIN_PLAY_MUSIC_TRACKS, UPLOAD_PLAY_LIBRARY_MEDIA } from 'api/graphql/playMusic';
import { toJsonArg } from 'common/utils/graphqlJson';

const CATEGORIES = ['yoga', 'mobility', 'custom'];

// Forma común de los dos videos de una práctica. `null` en las marcas = archivo entero.
const EMPTY_CLIP = { url: null, startMs: null, endMs: null, durationMs: null, hasAudio: false };
const EMPTY_INTRO = { ...EMPTY_CLIP, readyMs: null, required: false };

// Segundos de lo que realmente se ve de un clip (tramo recortado, o archivo entero). 0 = aún no
// se sabe. Se usa para avisar cuando el cronómetro y el video no duran lo mismo.
const clipSeconds = (clip) => {
  const start = typeof clip.startMs === 'number' ? clip.startMs : 0;
  let end = null;
  if (typeof clip.endMs === 'number') end = clip.endMs;
  else if (typeof clip.durationMs === 'number') end = clip.durationMs;

  if (end === null || end <= start) return 0;
  return Math.round((end - start) / 1000);
};

// Igual que en el editor de pasos: por debajo de 3 s de desfase nadie lo nota.
const SYNC_TOLERANCE_SECONDS = 3;

// Botones gemelos "Grabar" / "Subir archivo": secundarios, sin robarle el dorado al CTA.
const videoActionSx = {
  flex: 1,
  textTransform: 'none',
  fontWeight: 700,
  color: 'text.secondary',
  border: '1px solid',
  borderColor: 'hairline.main',
  borderRadius: 2,
  transition: 'color 200ms cubic-bezier(0.32,0.72,0,1), border-color 200ms cubic-bezier(0.32,0.72,0,1)',
  '&:hover': { color: 'gold.main', borderColor: 'hairline.strong' },
  '&:active': { transform: 'scale(0.98)' },
  '&.Mui-disabled': { color: 'text.disabled', borderColor: 'hairline.main' }
};

/**
 * OTRIX Play (admin/entrenadora) — editor de una práctica guiada en drawer lateral: datos
 * básicos, video general, música de la librería y el CONSTRUCTOR de secuencia dinámico
 * (tiempos por paso según el video de la entrenadora). Las prácticas de sistema conservan
 * su nombre por i18n (aquí se editan tiempos, videos, música y estado).
 */
function PlayRecoveryPracticeDrawer({ open, practice = null, onClose }) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  // Un solo <input type="file"> para los dos videos: este ref recuerda a cuál pertenece el
  // archivo que el usuario está eligiendo.
  const pickTargetRef = useRef(null);

  const isSystem = Boolean(practice?.key);
  const isBreathing = practice?.category === 'breathing';

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('custom');
  const [minutes, setMinutes] = useState(8);
  // Los dos videos viven como objeto y no como diez useState sueltos: al reemplazar el archivo
  // hay que limpiar sus marcas EN BLOQUE, y con estados separados es fácil olvidarse de uno y
  // dejar un recorte del video viejo aplicado sobre el nuevo.
  const [video, setVideo] = useState(EMPTY_CLIP);
  const [intro, setIntro] = useState(EMPTY_INTRO);
  const [musicTrackId, setMusicTrackId] = useState('');
  const [musicAlbumId, setMusicAlbumId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [steps, setSteps] = useState([]);
  // 'video' | 'intro' | null: identifica sobre CUÁL de los dos actúa cada modal y cada subida.
  const [uploadingTarget, setUploadingTarget] = useState(null);
  const [recorderTarget, setRecorderTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const { data: musicData } = useQuery(ADMIN_PLAY_MUSIC_TRACKS, { skip: !open, fetchPolicy: 'cache-and-network' });
  const { data: albumData } = useQuery(ADMIN_PLAY_MUSIC_ALBUMS, { skip: !open, fetchPolicy: 'cache-and-network' });
  const [uploadMedia] = useMutation(UPLOAD_PLAY_LIBRARY_MEDIA);
  const [savePractice, { loading: saving }] = useMutation(SAVE_PLAY_RECOVERY_PRACTICE, {
    refetchQueries: [{ query: ADMIN_PLAY_RECOVERY_PRACTICES }, { query: PLAY_RECOVERY_PRACTICES }]
  });

  useEffect(() => {
    if (!open) return;
    setTitle(practice?.title || '');
    setDescription(practice?.description || '');
    setCategory(practice?.category && practice.category !== 'breathing' ? practice.category : 'custom');
    setMinutes(practice?.minutes || 8);
    setVideo({
      url: practice?.video_url || null,
      startMs: practice?.video_start_ms ?? null,
      endMs: practice?.video_end_ms ?? null,
      durationMs: practice?.video_duration_ms ?? null,
      hasAudio: Boolean(practice?.video_has_audio)
    });
    setIntro({
      url: practice?.intro_video_url || null,
      startMs: practice?.intro_start_ms ?? null,
      endMs: practice?.intro_end_ms ?? null,
      durationMs: practice?.intro_duration_ms ?? null,
      hasAudio: Boolean(practice?.intro_has_audio),
      readyMs: practice?.intro_ready_ms ?? null,
      required: Boolean(practice?.intro_required)
    });
    setMusicTrackId(practice?.music_track?.id ? String(practice.music_track.id) : '');
    setMusicAlbumId(practice?.music_album?.id ? String(practice.music_album.id) : '');
    setIsActive(practice ? Boolean(practice.is_active) : true);
    setSteps(
      (practice?.steps || []).map((s) => ({
        key: s.key || null,
        title: s.title || '',
        description: s.description || '',
        type: s.type || 'exercise',
        duration_seconds: s.duration_seconds || 60,
        video_url: s.video_url || null,
        image_url: s.image_url || null,
        // `??` y no `||`: un recorte que empieza en el milisegundo 0 es un recorte real, y con
        // `||` se convertiría en null, o sea "sin recorte".
        video_start_ms: s.video_start_ms ?? null,
        video_end_ms: s.video_end_ms ?? null,
        video_duration_ms: s.video_duration_ms ?? null,
        video_has_audio: Boolean(s.video_has_audio),
        // Se arrastra tal cual (string JSON del backend) para no perderlo al guardar.
        pattern: s.pattern || null
      }))
    );
    setSubmitted(false);
  }, [open, practice]);

  const notify = (variant, message) =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `rec-${Date.now()}`, variant, message })));

  // Camino único para los dos videos, vengan de donde vengan: grabados en la app o elegidos del
  // equipo. `target` dice si es el video general de la práctica o el saludo de bienvenida.
  const uploadClip = async (target, file) => {
    // Duración y peso se revisan ANTES de subir: nada de esperar 200 MB para un rechazo.
    const problem = await validateVideoFile(file, t);
    if (problem) {
      notify('error', problem);
      return;
    }

    setUploadingTarget(target);
    try {
      const { data } = await uploadMedia({ variables: { file } });
      const url = data?.uploadPlayLibraryMedia?.url || null;
      // Archivo nuevo, marcas a cero: un recorte medido sobre el video anterior apuntaría a un
      // segundo que en este quizá ni existe, y el reproductor se quedaría en negro.
      const apply = (current) => ({ ...current, ...EMPTY_CLIP, url });
      if (target === 'intro') setIntro((current) => ({ ...apply(current), readyMs: null }));
      else setVideo(apply);
    } catch {
      notify('error', t('recoveryAdmin.videoError', { ns: 'play', defaultValue: 'No pudimos subir el video.' }));
    } finally {
      setUploadingTarget(null);
    }
  };

  const onPickVideo = async (e) => {
    const file = e.target.files?.[0];
    const target = pickTargetRef.current || 'video';
    e.target.value = '';
    pickTargetRef.current = null;
    if (file) await uploadClip(target, file);
  };

  const openPicker = (target) => {
    pickTargetRef.current = target;
    videoRef.current?.click();
  };

  // Quitar el video se lleva TAMBIÉN sus marcas: un recorte huérfano se aplicaría al siguiente
  // archivo que se suba y descuadraría el arranque.
  const removeClip = () => {
    if (removeTarget === 'intro') setIntro({ ...EMPTY_INTRO });
    else if (removeTarget === 'video') setVideo({ ...EMPTY_CLIP });
    setRemoveTarget(null);
  };

  const previewClip = previewTarget === 'intro' ? intro : video;

  const stepSeconds = steps.reduce((acc, s) => acc + (s.duration_seconds || 0), 0);

  const handleSave = async () => {
    setSubmitted(true);
    if (!isSystem && !title.trim()) return;
    if (!isBreathing && steps.length === 0) return;

    try {
      await savePractice({
        variables: {
          input: {
            id: practice?.id || null,
            title: isSystem ? practice?.title || null : title.trim(),
            description: isSystem ? practice?.description || null : description.trim() || null,
            category: isBreathing ? 'breathing' : category,
            icon: practice?.icon || null,
            minutes: isBreathing ? minutes : Math.max(1, Math.round(stepSeconds / 60)),
            // Campos `JSON`: viajan SERIALIZADOS (el escalar del backend hace json_decode).
            breathing_pattern: toJsonArg(practice?.breathing_pattern || null),
            video_url: video.url,
            video_start_ms: video.startMs,
            video_end_ms: video.endMs,
            video_duration_ms: video.durationMs,
            video_has_audio: video.hasAudio,
            // El saludo de bienvenida: sin `intro_video_url` el runner entra directo a la
            // práctica, así que quitarlo es simplemente dejarlo en null.
            intro_video_url: intro.url,
            intro_start_ms: intro.startMs,
            intro_end_ms: intro.endMs,
            intro_duration_ms: intro.durationMs,
            intro_ready_ms: intro.readyMs,
            intro_has_audio: intro.hasAudio,
            intro_required: intro.required,
            music_track_id: musicTrackId || null,
            // Sin esto el álbum se PERDÍA en cada guardado: el resolver hace `?? null`, así que
            // un input que no lo mande equivale a borrarlo, y no había forma de volver a ponerlo.
            music_album_id: musicAlbumId || null,
            is_active: isActive,
            position: practice?.position || 0,
            steps: isBreathing
              ? []
              : steps.map((s, i) => ({
                  position: i + 1,
                  key: s.key,
                  title: s.title || null,
                  description: s.description || null,
                  type: s.type,
                  duration_seconds: s.duration_seconds,
                  video_url: s.video_url,
                  image_url: s.image_url,
                  // El backend REEMPLAZA los pasos (delete + insert): lo que no viaje aquí se
                  // pierde en cada guardado. Es la misma trampa que ya mordió con `pattern`.
                  video_start_ms: s.video_start_ms ?? null,
                  video_end_ms: s.video_end_ms ?? null,
                  video_duration_ms: s.video_duration_ms ?? null,
                  video_has_audio: Boolean(s.video_has_audio),
                  // Sin esto el patrón de respiración del paso se perdía en cada guardado.
                  pattern: toJsonArg(s.pattern || null)
                }))
          }
        }
      });
      notify('success', t('recoveryAdmin.saved', { ns: 'play', defaultValue: 'Práctica guardada.' }));
      onClose();
    } catch {
      notify('error', t('recoveryAdmin.saveError', { ns: 'play', defaultValue: 'No pudimos guardar la práctica. Intenta de nuevo.' }));
    }
  };

  // Puente entre el patch plano de VideoTrimField ({ startMs, endMs, durationMs, hasAudio,
  // readyMs }) y el estado del drawer. Se copian solo las claves PRESENTES: el recortador manda
  // patches parciales y un `patch.endMs` ausente no puede borrar el fin ya marcado.
  const applyTrim = (setter) => (patch) => setter((current) => ({ ...current, ...patch }));

  const breathingClip = clipSeconds(video);
  const breathingSynced = Math.abs(breathingClip - minutes * 60) <= SYNC_TOLERANCE_SECONDS;

  /**
   * Bloque de UNO de los dos videos: subir/grabar, ver, quitar y recortar.
   *
   * Es una función que devuelve JSX y NO un componente anidado a propósito: un componente
   * declarado dentro del padre es un tipo nuevo en cada render, así que React desmontaría y
   * volvería a montar el <video> del recortador en cada tecla que se pulse en el formulario.
   */
  const renderClipBlock = ({ target, clip, title: blockTitle, hint, showReadyMark = false, onTrim, extra = null }) => {
    const busy = uploadingTarget === target;

    return (
      <Box sx={{ p: 1.75, borderRadius: '12px', border: '1px dashed', borderColor: 'hairline.main', backgroundColor: 'surface.overlay' }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="caption" fontWeight={800}>
            {blockTitle}
          </Typography>
          {clip.url && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'gold.main', flex: 'none' }}>
              <CheckCircleOutline sx={{ fontSize: 15 }} />
              <Typography variant="caption" fontWeight={700}>
                {t('recoveryAdmin.videoReady', { ns: 'play', defaultValue: 'Video cargado' })}
              </Typography>
            </Stack>
          )}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
          <Button
            size="small"
            startIcon={<Videocam sx={{ fontSize: 16 }} />}
            onClick={() => setRecorderTarget(target)}
            disabled={uploadingTarget !== null}
            sx={videoActionSx}>
            {t('recoveryAdmin.videoRecord', { ns: 'play', defaultValue: 'Grabar' })}
          </Button>
          <Button
            size="small"
            startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
            onClick={() => openPicker(target)}
            disabled={uploadingTarget !== null}
            sx={videoActionSx}>
            {t('recoveryAdmin.videoPick', { ns: 'play', defaultValue: 'Subir archivo' })}
          </Button>
        </Stack>

        {clip.url && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
            <Button
              size="small"
              startIcon={<PlayCircleOutline sx={{ fontSize: 16 }} />}
              onClick={() => setPreviewTarget(target)}
              sx={{ ...videoActionSx, flex: 1 }}>
              {t('recoveryAdmin.videoView', { ns: 'play', defaultValue: 'Ver' })}
            </Button>
            <IconButton
              size="small"
              onClick={() => setRemoveTarget(target)}
              disabled={uploadingTarget !== null}
              aria-label={t('recoveryAdmin.videoRemove', { ns: 'play', defaultValue: 'Quitar el video' })}
              sx={{ color: 'sessionStatus.absent.fg', flex: 'none', border: '1px solid', borderColor: 'hairline.main', borderRadius: 2 }}>
              <DeleteOutline sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        )}

        {busy ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
            <CircularProgress size={12} sx={{ color: 'gold.main' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              {t('musicAdmin.uploading', { ns: 'play', defaultValue: 'Subiendo…' })}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
            {hint}{' '}
            {t('recoveryAdmin.videoLimits', {
              ns: 'play',
              minutes: Math.round(MAX_VIDEO_SECONDS / 60),
              mb: MAX_VIDEO_MB,
              defaultValue: 'Máximo {{minutes}} minutos y {{mb}} MB.'
            })}
          </Typography>
        )}

        {clip.url && (
          <Box sx={{ mt: 1.25 }}>
            <VideoTrimField
              videoUrl={clip.url}
              startMs={clip.startMs}
              endMs={clip.endMs}
              durationMs={clip.durationMs}
              hasAudio={clip.hasAudio}
              readyMs={showReadyMark ? clip.readyMs : null}
              showReadyMark={showReadyMark}
              onChange={onTrim}
            />
          </Box>
        )}

        {extra}
      </Box>
    );
  };

  const tracks = musicData?.adminPlayMusicTracks || [];
  const albums = albumData?.adminPlayMusicAlbums || [];
  const displayTitle = isSystem
    ? t(`recovery.blocks.${practice.key}.title`, { ns: 'play' })
    : title || t('recoveryAdmin.newTitle', { ns: 'play', defaultValue: 'Nueva práctica' });

  return (
    <StyledPlayDrawer anchor="right" open={open} onClose={onClose}>
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={{ px: 2.5, py: 2, position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'surface.sidebar', borderBottom: '1px solid', borderColor: 'hairline.main' }}>
          <Typography variant="h6" fontWeight={800} letterSpacing="-0.02em" noWrap>
            {displayTitle}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label={t('recovery.close', { ns: 'play' })} sx={{ flex: 'none', border: '1px solid', borderColor: 'hairline.main' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <Stack spacing={2.5} sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5 }}>
          {isSystem && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('recoveryAdmin.systemHint', { ns: 'play', defaultValue: 'Práctica del sistema: el nombre y la descripción vienen de la app. Puedes ajustar tiempos, videos, música y visibilidad.' })}
            </Typography>
          )}

          {!isSystem && (
            <>
              <TextField
                label={t('recoveryAdmin.title', { ns: 'play', defaultValue: 'Nombre de la práctica' })}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                error={submitted && !title.trim()}
                helperText={submitted && !title.trim() ? t('musicAdmin.titleRequired', { ns: 'play', defaultValue: 'Requerido' }) : ''}
                fullWidth
              />
              <TextField
                label={t('recoveryAdmin.description', { ns: 'play', defaultValue: 'Descripción breve' })}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                select
                label={t('recoveryAdmin.category', { ns: 'play', defaultValue: 'Categoría' })}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                fullWidth>
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {t(`recoveryAdmin.categories.${c}`, { ns: 'play', defaultValue: c })}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          {isBreathing && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField
                type="number"
                label={t('recoveryAdmin.minutes', { ns: 'play', defaultValue: 'Minutos' })}
                value={minutes}
                onChange={(e) => setMinutes(Math.max(1, Number(e.target.value) || 1))}
                inputProps={{ min: 1, max: 60 }}
                sx={{ maxWidth: 160, flex: 'none' }}
              />
              {/* En respiración no hay pasos: la duración de la práctica son estos minutos, así
                  que es aquí donde el cronómetro se puede desfasar del video. */}
              {video.url && breathingClip > 0 && !breathingSynced && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ flex: 1, minWidth: 0, color: 'sessionStatus.pending.fg' }}>
                    {t('recoveryAdmin.syncOffMinutes', {
                      ns: 'play',
                      minutes,
                      clip: Math.round((breathingClip / 60) * 10) / 10,
                      defaultValue: 'La práctica dura {{minutes}} min y el video {{clip}} min: el video se repetirá.'
                    })}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Sync sx={{ fontSize: 16 }} />}
                    onClick={() => setMinutes(Math.max(1, Math.min(60, Math.round(breathingClip / 60))))}
                    sx={{ textTransform: 'none', fontWeight: 700, color: 'gold.main', flex: 'none', '&:active': { transform: 'scale(0.98)' } }}>
                    {t('recoveryAdmin.syncApply', { ns: 'play', defaultValue: 'Igualar al video' })}
                  </Button>
                </Stack>
              )}
            </Stack>
          )}

          {/* Los dos videos de la práctica. El general es lo que se ve durante los pasos que no
              traen video propio; el de bienvenida es el saludo previo, con su puerta de "listo".
              Ambos se pueden VER y QUITAR: antes solo se podían reemplazar a ciegas. */}
          <input ref={videoRef} type="file" accept="video/*" hidden onChange={onPickVideo} />

          {renderClipBlock({
            target: 'video',
            clip: video,
            title: t('recoveryAdmin.generalVideo', { ns: 'play', defaultValue: 'Video general (opcional)' }),
            hint: t('recoveryAdmin.generalVideoHint', {
              ns: 'play',
              defaultValue: 'Se muestra en los pasos que no tengan video propio.'
            }),
            onTrim: applyTrim(setVideo)
          })}

          {renderClipBlock({
            target: 'intro',
            clip: intro,
            title: t('recoveryAdmin.introVideo', { ns: 'play', defaultValue: 'Video de bienvenida (opcional)' }),
            hint: t('recoveryAdmin.introVideoHint', {
              ns: 'play',
              defaultValue: 'Se ve antes de empezar. Sin él, la práctica arranca de inmediato.'
            }),
            showReadyMark: true,
            onTrim: applyTrim(setIntro),
            extra: intro.url ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Switch
                  size="small"
                  checked={intro.required}
                  onChange={(e) => setIntro((current) => ({ ...current, required: e.target.checked }))}
                  color="secondary"
                  inputProps={{
                    'aria-label': t('recoveryAdmin.introRequired', {
                      ns: 'play',
                      defaultValue: 'Hay que ver la bienvenida antes de empezar'
                    })
                  }}
                />
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" fontWeight={700}>
                    {t('recoveryAdmin.introRequired', {
                      ns: 'play',
                      defaultValue: 'Hay que ver la bienvenida antes de empezar'
                    })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', lineHeight: 1.4 }}>
                    {t('recoveryAdmin.introRequiredHint', {
                      ns: 'play',
                      defaultValue: 'Apagado, el cliente puede empezar de una. El video igual se muestra.'
                    })}
                  </Typography>
                </Stack>
              </Stack>
            ) : null
          })}

          <TextField
            select
            size="small"
            label={t('recovery.detail.music', { ns: 'play', defaultValue: 'Música relajante' })}
            value={musicTrackId}
            onChange={(e) => setMusicTrackId(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}>
            <MenuItem value="">{t('recovery.detail.noMusic', { ns: 'play', defaultValue: 'Sin música' })}</MenuItem>
            {tracks.map((track) => (
              <MenuItem key={track.id} value={String(track.id)}>
                {track.title}
              </MenuItem>
            ))}
          </TextField>

          {/* Álbum de arranque: el backend ya lo soportaba y el runner ya sabe encolar la
              playlist entera, pero no había forma de elegirlo desde aquí. */}
          <TextField
            select
            size="small"
            label={t('recoveryAdmin.musicAlbum', { ns: 'play', defaultValue: 'Playlist de la práctica' })}
            value={musicAlbumId}
            onChange={(e) => setMusicAlbumId(e.target.value)}
            helperText={
              musicAlbumId
                ? t('recoveryAdmin.musicAlbumWins', {
                    ns: 'play',
                    defaultValue: 'La playlist manda sobre la pista suelta: entra la cola completa.'
                  })
                : t('recoveryAdmin.musicAlbumHint', {
                    ns: 'play',
                    defaultValue: 'Opcional. Puede ser una lista de voces de respiración.'
                  })
            }
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}>
            <MenuItem value="">{t('recoveryAdmin.noAlbum', { ns: 'play', defaultValue: 'Sin playlist' })}</MenuItem>
            {albums.map((album) => (
              <MenuItem key={album.id} value={String(album.id)}>
                {album.title}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" fontWeight={700}>
              {t('recoveryAdmin.active', { ns: 'play', defaultValue: 'Visible para los clientes' })}
            </Typography>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="secondary" />
          </Stack>

          {!isBreathing && (
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                {t('recoveryAdmin.sequence', { ns: 'play', defaultValue: 'Secuencia de pasos' })}
                {submitted && steps.length === 0 && (
                  <Box component="span" sx={{ color: 'gold.main', ml: 0.5 }}>
                    · {t('recoveryAdmin.stepsRequired', { ns: 'play', defaultValue: 'agrega al menos un paso' })}
                  </Box>
                )}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <PlayRecoveryStepEditor steps={steps} onChange={setSteps} practiceKey={practice?.key || null} />
              </Box>
            </Box>
          )}
        </Stack>

        <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.sidebar' }}>
          <Button fullWidth onClick={handleSave} disabled={saving || uploadingTarget !== null} sx={{ ...premiumButtonSx, py: 1.2 }}>
            {saving
              ? t('musicAdmin.saving', { ns: 'play', defaultValue: 'Guardando…' })
              : t('recoveryAdmin.save', { ns: 'play', defaultValue: 'Guardar práctica' })}
          </Button>
        </Box>
      </Stack>

      <VideoRecorderModal
        open={recorderTarget !== null}
        onClose={() => setRecorderTarget(null)}
        onRecorded={(file) => uploadClip(recorderTarget, file)}
        title={
          recorderTarget === 'intro'
            ? t('recoveryAdmin.recorder.titleIntro', { ns: 'play', defaultValue: 'Grabar la bienvenida' })
            : t('recoveryAdmin.recorder.titleGeneral', { ns: 'play', defaultValue: 'Grabar video general' })
        }
      />

      <VideoPreviewModal
        open={previewTarget !== null}
        src={previewClip.url}
        startMs={previewClip.startMs}
        endMs={previewClip.endMs}
        hasAudio={previewClip.hasAudio}
        title={
          previewTarget === 'intro'
            ? t('recoveryAdmin.introVideo', { ns: 'play', defaultValue: 'Video de bienvenida (opcional)' })
            : t('recoveryAdmin.generalVideo', { ns: 'play', defaultValue: 'Video general (opcional)' })
        }
        onClose={() => setPreviewTarget(null)}
      />

      <ConfirmDeleteModal
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={removeClip}
        title={t('recoveryAdmin.videoRemoveTitle', { ns: 'play', defaultValue: 'Quitar el video' })}
        message={
          removeTarget === 'intro'
            ? t('recoveryAdmin.introRemoveMessage', {
                ns: 'play',
                defaultValue: 'La práctica pasará a arrancar de inmediato, sin bienvenida.'
              })
            : t('recoveryAdmin.generalRemoveMessage', {
                ns: 'play',
                defaultValue: 'Los pasos sin video propio se quedarán con su imagen o el ícono.'
              })
        }
        confirmLabel={t('recoveryAdmin.videoRemove', { ns: 'play', defaultValue: 'Quitar el video' })}
      />
    </StyledPlayDrawer>
  );
}

PlayRecoveryPracticeDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  // Esta versión de `require-default-props` todavía exige `defaultProps`, que React 18.3 ya
  // deprecó en componentes de función: el valor por defecto va en la firma.
  // eslint-disable-next-line react/require-default-props
  practice: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default PlayRecoveryPracticeDrawer;
